/**
 * Represent the user-visible information required to make predictions
 * in a {@link Forecast}.
 *
 * This is very close to a {@link RuleTemplate}. Nevertheless, while a
 * {@link RuleTemplate} is used for computation, an assumption is only
 * used as a way to explain what is happening to the user. Differences
 * between the two models include:
 *
 * - Assumption is for the frontend only, the concept doesn't exist in
 *  the backend;
 *
 * - An assumption is generally created from a single rule template
 *   but it can also be a merge of 2 rule templates. This happens when
 *   a {@link AbstractPredictionRuleModel} and {@link
 *   BalanceRuleModel} are added to the same cell: the user considers
 *   that as a single rule so we make 1 assumption out of these 2 rule
 *   templates.
 *
 * @param {string} rowId - Identifier of the row on which the
 *   assumption applies
 * @param {Account} [account] - If the assumption is known to be linked
 *   to an account, this can be stored here. This is especially useful
 *   for assumptions based on {@link ManualJournalEntryTransaction}
 *   because the account of a transaction has nothing to do with the
 *   account of the row at `rowId`
 * @param {MonthPeriod} start - The forecast period (i.e., column) at
 *   which the assumption starts
 * @param {MonthPeriod|Infinity} [end] - The forecast period (i.e.,
 *   column) at which the assumption stops impacting predictions. For
 *   an assumption that impacts the forecast until its end, Infinity
 *   must be used as value
 * @param {string} name - The name of a ManualEntryRuleModel or of a
 *   ManualJournalEntryRuleModel
 * @param {Class<AbstractRuleModel>} ruleModelClass - The type of rule
 *   model used to compute the numbers
 * @param {float} value - Can be the `initialValue` of a {@link
 *   ConstantPredictionRuleModel} or the `value` of a {@link
 *   ManualEntryRuleModel}
 * @param {float} [growthPercentage] - The increase/decrease to compute a
 * month based on the value of the previous one
 * @param {VatRate} [vatRate] - Value-added tax that is applied
 * @param {number} [historicalInput] - Number of previous months
 *   to consider for the regression
 * @param {int[]} [paymentSpread] - Numbers between 0 and 100. The sum of these
 *   numbers must be 100
 * @param {string} [description] - An HTML string the accountant typed
 *   to explain the reason for the rule
 */
export default class Assumption {
	constructor({
		rowId,
		account,
		start,
		end,
		name,
		ruleModelClass,
		value,
		growthPercentage,
		vatRate,
		historicalInput,
		paymentSpread,
		description,
	}) {
		this._rowId = rowId;
		this._account = account;
		this._start = start;
		this._end = end;
		this._name = name;
		this._ruleModelClass = ruleModelClass;
		this._value = value;
		this._growthPercentage = growthPercentage;
		this._vatRate = vatRate;
		this._historicalInput = historicalInput;
		this._paymentSpread = paymentSpread;
		this._description = description;
	}

	getRowId() {
		return this._rowId;
	}

	getAccount() {
		return this._account;
	}

	getStart() {
		return this._start;
	}

	getEnd() {
		return this._end;
	}

	getName() {
		return this._name;
	}

	getRuleModelLabel() {
		return this._ruleModelClass.getLabel();
	}

	getValue() {
		return this._value;
	}

	getGrowthPercentage() {
		return this._growthPercentage;
	}

	getVatRateValue() {
		return this._vatRate?.getValue();
	}

	getHistoricalInput() {
		return this._historicalInput;
	}

	getPaymentSpread() {
		return this._paymentSpread;
	}

	getDescription() {
		return this._description;
	}

	readSettingsFromBalanceRuleModel(balanceRuleModel) {
		this._vatRate = balanceRuleModel.getVatRate();
		this._paymentSpread = balanceRuleModel.getPaymentSpread();
	}

	findCell({ forecast }) {
		let row = forecast.getRowByStableId(this._rowId);
		const forecastReport = forecast.getReportFromId(row.getReportId());
		const column = forecastReport.getColumn({ start: this._start });

		// if possible, return the row associated with the account
		// instead of the row associated with the rule template:
		if (this._account) {
			const accountNumber = this._account.getNumber();
			const accountRow =
				forecastReport.getVisibleRowForAccountNumber(accountNumber);

			if (accountRow) {
				row = accountRow;
			}
		}

		return { row, report: forecastReport, column };
	}
}
